'use client';

import { useAppDispatch, useAppSelector } from '@akinon/next/redux/hooks';
import { useLocalization } from '@akinon/next/hooks';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import ProductCard from './product-card';
import { Button, Link } from '@theme/components';
import {
  clearCompareList,
  getCompareProducts,
  setLimitWarning,
  setCategoryWarning,
  MAX_COMPARE_PRODUCTS
} from '@theme/redux/reducers/compare';

export default function CompareProducts() {
  const { t } = useLocalization();
  const { productsPKList, limitWarning, categoryWarning } = useAppSelector(
    (state) => state.compare
  );
  const [hasCompare, setHasCompare] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const dispatch = useAppDispatch();

  const canCompare = productsPKList.length >= 2;

  useEffect(() => {
    setIsMounted(true);
    if (typeof window !== 'undefined') {
      dispatch(getCompareProducts());
    }
  }, [dispatch]);

  useEffect(() => {
    setHasCompare(productsPKList.length > 0);
  }, [productsPKList.length]);

  useEffect(() => {
    if (!limitWarning) return;
    const timeout = setTimeout(() => {
      dispatch(setLimitWarning(false));
    }, 4000);
    return () => clearTimeout(timeout);
  }, [limitWarning, dispatch]);

  useEffect(() => {
    if (!categoryWarning) return;
    const timeout = setTimeout(() => {
      dispatch(setCategoryWarning(false));
    }, 4000);
    return () => clearTimeout(timeout);
  }, [categoryWarning, dispatch]);

  return (
    <>
      {isMounted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: hasCompare ? 1 : 0,
            y: hasCompare ? 0 : 20
          }}
          transition={{ duration: 0.3 }}
          className={twMerge(
            'fixed bottom-0 right-0 z-[9999] w-full overflow-x-hidden bg-white shadow-lg flex items-center transition-opacity duration-300 py-3 lg:pb-1 lg:pt-3 px-[5%] border-t border-[#A6A6A6]',
            hasCompare ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center w-full gap-2 lg:gap-5 lg:w-[85%]">
            <div className="flex items-start gap-5 py-1 lg:py-0">
              {productsPKList.map((pk) => (
                <ProductCard productPk={pk} key={pk} />
              ))}
            </div>

            {(limitWarning || categoryWarning) && (
              <div className="order-first w-full mb-2 lg:order-none lg:w-auto lg:mb-0 border border-error-200 px-4 py-3 rounded-[4px] bg-error-300">
                <span className="block text-xs leading-tight text-error lg:flex-1 lg:text-center">
                  {t(
                    categoryWarning
                      ? 'common.compare_category_mismatch'
                      : 'common.compare_max_reached'
                  )}
                </span>
              </div>
            )}
            <div className="lg:hidden block w-full h-px bg-gray-250" />
            <div className="w-full flex items-center justify-between lg:justify-normal lg:w-auto py-1 lg:py-0 lg:gap-[90px]">
              <span className="text-sm whitespace-nowrap">{`${productsPKList.length}/${MAX_COMPARE_PRODUCTS} ürün seçili`}</span>

              <div className="flex items-center gap-4">
                <Button
                  className="font-medium text-sm underline"
                  onClick={() => {
                    dispatch(clearCompareList());
                  }}
                >
                  Vazgeç
                </Button>
                {canCompare ? (
                  <Link
                    href={'/product-comparison'}
                    className="bg-primary-black text-white text-sm h-auto py-[15px] px-[22px] rounded-full hover:bg-primary-black/90"
                  >
                    Karşılaştır
                  </Link>
                ) : (
                  <span
                    aria-disabled="true"
                    className="bg-gray-250 text-gray text-sm h-auto py-[15px] px-[22px] rounded-full cursor-not-allowed select-none"
                  >
                    Karşılaştır
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}
